import {
  usersGetTwinResponder,
  usersGetTwinResponderLabels,
  usersGetTwinResponderStats,
  usersGetTwinResponderThread,
  usersGetTwinResponderThreads,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useActiveOrganization } from './use-organization'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { twinResponderLabelsAtom } from 'state'
import {
  Feature,
  TwinResponder,
  TwinResponderLabel,
  TwinResponderStats,
  TwinResponderThread,
  TwinResponderThreadDraftStatus,
  UsersGetTwinResponderThreadsFormat,
} from 'api/model'
import { featurePermissionsAtomFamily } from 'state/permission'

function useAdvancedSettings() {
  const [moveSalesEmailsToLabel, setMoveSalesEmailsToLabel] = useState<boolean>(false)
  const [moveNoReplyEmailsToLabel, setMoveNoReplyEmailsToLabel] = useState<boolean>(false)
  const [labelForDraftEmails, setLabelForDraftEmails] = useState<string>('')
  const [labelForSalesEmails, setLabelForSalesEmails] = useState<string>('')
  const [labelForNoReplyEmails, setLabelForNoReplyEmails] = useState<string>('')
  const [isAutoSendEnabled, setIsAutoSendEnabled] = useState<boolean>(false)

  // clear checkbox state if label is empty
  useEffect(() => {
    if (labelForSalesEmails === '') {
      setMoveSalesEmailsToLabel(false)
    }
  }, [labelForSalesEmails])

  // clear checkbox state if label is empty
  useEffect(() => {
    if (labelForNoReplyEmails === '') {
      setMoveNoReplyEmailsToLabel(false)
    }
  }, [labelForNoReplyEmails])

  return {
    moveSalesEmailsToLabel,
    setMoveSalesEmailsToLabel,
    moveNoReplyEmailsToLabel,
    setMoveNoReplyEmailsToLabel,
    labelForDraftEmails,
    setLabelForDraftEmails,
    labelForSalesEmails,
    setLabelForSalesEmails,
    labelForNoReplyEmails,
    setLabelForNoReplyEmails,
    isAutoSendEnabled,
    setIsAutoSendEnabled,
  }
}

function useFetchTwinResponder(twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [twinResponder, setTwinResponder] = useState<TwinResponder | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchTwinResponder = useCallback(async () => {
    if (!activeOrganization || !twinId) {
      return
    }

    setIsLoading(true)

    try {
      const response = await usersGetTwinResponder(activeOrganization, twinId)
      setTwinResponder(response.data)
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, twinId])

  useLayoutEffect(() => {
    refetchTwinResponder()
  }, [refetchTwinResponder])

  return {
    isLoading,
    twinResponder,
    error,
    setTwinResponder,
    refetchTwinResponder,
  }
}

function useFetchTwinResponderLabels(twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [twinResponderLabels, setTwinResponderLabels] = useRecoilState<TwinResponderLabel[]>(twinResponderLabelsAtom)
  const hasPermissions = useRecoilValue(featurePermissionsAtomFamily(Feature.email_responder))?.isPermitted ?? false
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !twinId || !hasPermissions) {
        return
      }

      setIsLoading(true)

      try {
        const response = await usersGetTwinResponderLabels(activeOrganization, twinId)

        // sort labels so that Inbox label is always first
        const sortedLabels = response.data.sort((a, b) => {
          if (a.name === 'INBOX') {
            return -1
          } else if (b.name === 'INBOX') {
            return 1
          } else {
            return 0
          }
        })

        setTwinResponderLabels(sortedLabels)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setTwinResponderLabels, twinId, hasPermissions])

  const selectedTwinResponderLabels = twinResponderLabels.filter((label) => label.is_selected)

  return {
    isLoading,
    twinResponderLabels,
    selectedTwinResponderLabels,
    error,
    setTwinResponderLabels,
  }
}

function useFetchTwinResponderStats(twinId: string, from: string, to: string) {
  const [activeOrganization] = useActiveOrganization()
  const [twinResponderStats, setTwinResponderStats] = useState<TwinResponderStats | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !twinId || !from || !to) {
        return
      }

      setIsLoading(true)

      try {
        const response = await usersGetTwinResponderStats(activeOrganization, twinId, {
          from,
          to,
        })
        setTwinResponderStats(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setTimeout(() => {
          setIsLoading(false)
        }, 1000)
      }
    }

    fetchData()
  }, [activeOrganization, from, to, twinId])

  return {
    isLoading,
    twinResponderStats,
    error,
  }
}

// fetches thread with emails property populated
function useFetchThread(threadId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [thread, setThread] = useState<TwinResponderThread | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !threadId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await usersGetTwinResponderThread(activeOrganization, threadId)
        setThread(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setThread, threadId])

  return {
    isLoading,
    thread,
    error,
    setThread,
  }
}

// this is used in the NavBar when showing the number of drafts that the user has for their Gmail
function useFetchDraftedThreadCount(twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [draftedThreadCount, setDraftedThreadCount] = useState<number | null>(null)

  const refetchThreads = useCallback(async () => {
    if (!activeOrganization || !twinId) {
      return
    }

    setIsLoading(true)

    try {
      const response = await usersGetTwinResponderThreads(activeOrganization, twinId, {
        count_only: true,
        draft_status: TwinResponderThreadDraftStatus.drafted,
        format: UsersGetTwinResponderThreadsFormat.short,
      })
      setDraftedThreadCount(response.data.total_count_drafted)
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, twinId])

  useLayoutEffect(() => {
    refetchThreads()
  }, [refetchThreads])

  return {
    isLoading,
    error,
    draftedThreadCount,
    refetchThreads,
  }
}

// threads are fetched without the email field populated due to its large size
function useFetchThreads(
  twinId: string,
  currentPage: number,
  label: string,
  draftStatus: TwinResponderThreadDraftStatus
) {
  const [activeOrganization] = useActiveOrganization()
  const [threads, setThreads] = useState<TwinResponderThread[]>([])
  const [totalDraftedCount, setTotalDraftedCount] = useState<number>(0)
  const [totalNotDraftedCount, setTotalNotDraftedCount] = useState<number>(0)
  const [totalSentCount, setTotalSentCount] = useState<number>(0)

  const [threadsPerPage, setThreadsPerPage] = useState<number>(25) // default to 25

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchThreads = useCallback(async () => {
    if (!activeOrganization || !twinId) {
      return
    }

    setIsLoading(true)

    try {
      const response = await usersGetTwinResponderThreads(activeOrganization, twinId, {
        // we're doing +1 for currentPage because the TablePagination component in MUI expects that the currentPage is 0-indexed whereas our backend is 1-indexed
        page: currentPage + 1,
        per_page: threadsPerPage,
        format: UsersGetTwinResponderThreadsFormat.short,
        label: label || undefined,
        draft_status: draftStatus,
      })
      setThreads(response.data.threads)
      setTotalDraftedCount(response.data.total_count_drafted)
      setTotalNotDraftedCount(response.data.total_count_not_drafted)
      setTotalSentCount(response.data.total_count_sent)
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, setThreads, twinId, currentPage, label, draftStatus, threadsPerPage])

  useLayoutEffect(() => {
    refetchThreads()
  }, [refetchThreads])

  return {
    isLoading,
    threads,
    error,
    setThreads,
    totalDraftedCount,
    totalNotDraftedCount,
    refetchThreads,
    threadsPerPage,
    setThreadsPerPage,
    setTotalDraftedCount,
    setTotalNotDraftedCount,
    setTotalSentCount,
    totalSentCount,
  }
}

export {
  useAdvancedSettings,
  useFetchDraftedThreadCount,
  useFetchTwinResponder,
  useFetchTwinResponderLabels,
  useFetchThread,
  useFetchThreads,
  useFetchTwinResponderStats,
}
